import Box from '@mui/material/Box';
import Container from '@mui/material/Container';

import {
  PageIntroCopy,
  type PageIntroCopyProps,
  PageIntroTrustStrip,
} from '@/components/molecules';
import { type PageIntroPlainLayout } from '@/hooks/usePageIntroLayout';

import { BAND_PADDING, STRIP_GAP } from './pageIntroBand';

type Props = PageIntroCopyProps &
  Pick<PageIntroPlainLayout, 'highlights' | 'onPhone'> & { compact?: boolean };

/**
 * The opening band of a page that has no media: one column of type on the
 * page's own surface, closed by a rule.
 *
 * This is what the legal, cart, checkout and quotation pages get, and it is
 * the right answer for them — a slideshow over the Terms of Use is decoration,
 * not communication. What it is not any more is a lesser version of the
 * cinematic band: it shares the rhythm, the headline measure and the trust
 * strip, so the two read as one component with the lights on or off.
 *
 * Below `md` it is also what the cinematic band becomes, which is the sharper
 * version of the same point: at that width the two layouts differ by nothing
 * but a rule, because a stage needs a second column and a phone has not got
 * one. The shared tokens in `pageIntroBand.ts` are what make that true rather
 * than approximately true.
 *
 * It will draw a strip if the page passes proof points. None of them do today,
 * which is deliberate rather than pending: `content/stats.ts` is still marked
 * PLACEHOLDER, and a band that invents a figure for a visitor about to spend
 * millions of shillings is a lie before it is a design problem.
 */
export function PageIntroPlain({ highlights, onPhone, compact = false, ...copy }: Props) {
  return (
    <Box
      component="header"
      sx={[
        {
          ...(compact ? { pt: { xs: 3, md: 4 }, pb: { xs: 3, md: 4 } } : BAND_PADDING),
          borderBottom: 1,
          borderColor: 'divider',
          ...(compact && {
            backgroundImage: [
              'radial-gradient(ellipse 48% 145% at 96% 0%, rgba(158,170,190,0.13) 0%, rgba(158,170,190,0) 76%)',
              'radial-gradient(ellipse 44% 130% at 4% 0%, rgba(var(--mui-palette-primary-mainChannel) / 0.09) 0%, rgba(var(--mui-palette-primary-mainChannel) / 0) 78%)',
            ].join(','),
          }),
          ...(compact && {
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: '0 0 auto',
              height: 3,
              bgcolor: 'primary.main',
            },
          }),
        },
        // Standard intros stay hidden on phones when their next section has
        // its own heading. Catalogue pages opt in with `keepOnPhone` so the
        // compact title remains visible above the filters and products.
        !onPhone && { display: { xs: 'none', md: 'block' } },
      ]}
    >
      <Container maxWidth="lg">
        <PageIntroCopy {...copy} stage={false} compact={compact} />
        {highlights && (
          <Box sx={{ mt: STRIP_GAP }}>
            <PageIntroTrustStrip items={highlights} stage={false} />
          </Box>
        )}
      </Container>
    </Box>
  );
}
